// Onboarding: diabetes profile, the three consents, the sensor, Dexcom, and the HbA1c lab result.
import { useEffect, useRef, useState } from 'preact/hooks'
import type { ConsentSource, DiabetesType, LabDraft, LabView, Me, Profile, Sensor, Spot, Treatment } from '../../../packages/contract/index.ts'
import { fill } from './i18n.ts'
import { ApiError, api, fmtDate, fmtPct, fmtWhen, toDate } from './lib.ts'
import { Crop, ErrorState, Icon, Skeleton, Spinner, useApp, useLoad } from './ui.tsx'

const consentOf = (me: Me, source: ConsentSource) => me.consents.find((c) => c.source === source)
const usesDexcom = (me: Me): boolean => me.profile?.sensor === 'g6' || me.profile?.sensor === 'g7'

/** The consent screens this customer goes through, in order. */
export const consentSteps = (me: Me): readonly ConsentSource[] => (usesDexcom(me) ? ['program', 'dexcom', 'lab'] : ['program', 'lab'])

// ───────────── profile ─────────────

type Draft = { diabetesType: DiabetesType | null; treatment: Treatment | null; year: string; sensor: Sensor | null }

export function ProfileScreen() {
  const app = useApp()
  const { t, me } = app
  const from = me?.profileFromPurchase ?? {}
  const start = me?.profile ?? from
  const [draft, setDraft] = useState<Draft>({ diabetesType: start.diabetesType ?? null, treatment: start.treatment ?? null, year: start.diagnosisYear ? String(start.diagnosisYear) : '', sensor: start.sensor ?? null })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const thisYear = new Date().getFullYear()
  const yearNum = Number(draft.year)
  const yearOk = /^\d{4}$/.test(draft.year) && yearNum >= 1920 && yearNum <= thisYear
  const ready = draft.diabetesType && draft.treatment && draft.sensor && yearOk
  const save = async () => {
    if (!draft.diabetesType || !draft.treatment || !draft.sensor || !yearOk) return
    setBusy(true)
    setError('')
    const profile: Profile = { diabetesType: draft.diabetesType, treatment: draft.treatment, diagnosisYear: yearNum, sensor: draft.sensor }
    try {
      const next = await api<Me>('/api/profile', { method: 'PUT', json: profile })
      app.setMe(next)
      app.nav(await app.nextStep(next))
    } catch { setError(t.errGeneric) } finally { setBusy(false) }
  }
  const group = <K extends 'diabetesType' | 'treatment' | 'sensor'>(key: K, legend: string, labels: Record<string, string>, two?: boolean) => (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend class="v-label" style={{ marginBottom: '10px' }}>{legend}</legend>
      <div class={two ? 'v-choices v-choices--2' : 'v-choices'}>
        {Object.entries(labels).map(([value, label]) => (
          <label key={value} class="v-choice">
            <input type="radio" name={key} checked={draft[key] === value} onChange={() => setDraft({ ...draft, [key]: value })} />
            <span class="v-choice-main">{label}</span>
            {from[key] === value && <span class="v-tag-from">{t.prFrom}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  )
  return (
    <>
      <div class="v-stack-s"><h1 class="v-h1">{t.prTitle}</h1><p class="v-lead">{t.prBody}</p></div>
      {group('diabetesType', t.prType, t.prTypes)}
      {group('treatment', t.prTreat, t.prTreats)}
      <label class="v-field">
        <span class="v-label">{t.prYear}</span>
        <span class="v-input-wrap"><input class="v-input v-num" inputMode="numeric" maxLength={4} value={draft.year} aria-invalid={draft.year.length === 4 && !yearOk ? 'true' : 'false'}
          onInput={(e) => setDraft({ ...draft, year: e.currentTarget.value.replace(/\D/g, '').slice(0, 4) })} />{yearOk && <span class="v-input-ok"><Icon n="check" /></span>}</span>
        {draft.year.length === 4 && !yearOk ? <span class="v-err">{fill(t.prYearErr, { y: thisYear })}</span> : from.diagnosisYear && String(from.diagnosisYear) === draft.year ? <span class="v-help">{t.prFrom}</span> : null}
      </label>
      {group('sensor', t.prSensor, t.prSensors, true)}
      {draft.sensor === 'none' && <div class="v-msg v-msg--brand v-pop"><Icon n="info" /><span>{t.prNone}</span></div>}
      {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
      <button class="v-btn v-btn--primary" type="button" disabled={!ready || busy} onClick={() => void save()}>{busy ? <Spinner /> : null}{t.cont}</button>
    </>
  )
}

// ───────────── consent, one screen per source ─────────────

export function ConsentScreen({ source }: { readonly source: ConsentSource }) {
  const app = useApp()
  const { t, me, lang } = app
  const [tick, setTick] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setTick(false); setError('') }, [source])
  if (!me) return null
  const c = t.consents[source]
  const view = consentOf(me, source)
  const steps = consentSteps(me)
  const index = Math.max(0, steps.indexOf(source))
  const done = async (next: Me) => {
    const back = app.afterConsent.current
    app.afterConsent.current = null
    app.nav(back ?? (await app.nextStep(next)))
  }
  const accept = async () => {
    if (!tick) return
    setBusy(true)
    setError('')
    try {
      const next = await api<Me>(`/api/consents/${source}`, { method: 'POST', json: { accepted: true } })
      app.setMe(next)
      app.unskip(`consent:${source}`)
      if (app.afterConsent.current) app.toast(t.wdGiven)
      await done(next)
    } catch { setError(t.errGeneric) } finally { setBusy(false) }
  }
  const skip = () => {
    if (app.afterConsent.current) { app.afterConsent.current = null; app.goBack('/settings'); return }
    if (source === 'program') { app.nav('/welcome'); return }
    app.skip(`consent:${source}`)
    void app.nextStep(me).then((p) => app.nav(p))
  }
  const rows = [
    { icon: 'tray' as const, title: t.csCollect, body: c.collect },
    { icon: 'eyeOff' as const, title: t.csNever, body: c.never },
    { icon: 'calc' as const, title: t.csCalc, body: c.calc },
    { icon: 'clock' as const, title: t.csKeep, body: c.keep },
    { icon: 'undo' as const, title: t.csWithdraw, body: t.csWithdrawV },
  ]
  return (
    <>
      <div class="v-between" style={{ flexWrap: 'wrap' }}>
        <span class="v-eyebrow">{fill(t.csStep, { n: index + 1, m: steps.length })}</span>
        <span class="v-pill"><Icon n="lock" s={16} />{t.csBadge}</span>
      </div>
      <div class="v-stack-s"><h1 class="v-h1">{c.title}</h1><p class="v-lead">{c.lead}</p></div>
      {source === 'program' && (
        <div class="v-card v-card--brand" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <span class="v-ico" style={{ background: 'var(--surface)', color: 'var(--brand-text)' }}><Icon n="down" /></span>
          <span class="v-stack-s" style={{ gap: '2px' }}><span class="v-h3">{t.promise}</span><span class="v-small" style={{ color: 'var(--ink-2)' }}>{t.promiseSub}</span></span>
        </div>
      )}
      <dl class="v-kv v-card v-card--flat" style={{ padding: '14px 16px', fontSize: '14px' }}>
        <dt>{t.csFor}</dt><dd>{view?.insurer ?? me.insurer}</dd>
        {view?.endsAt && <><dt>{t.csUntil}</dt><dd>{fill(t.csUntilV, { d: fmtDate(view.endsAt, lang) })}</dd></>}
        <dt>{t.csProc}</dt><dd>Sherpa+</dd>
      </dl>
      <div class="v-stack-s">
        <h2 class="v-h3">{t.csWho}</h2>
        <div class="v-who">
          <div class="v-who-col is-you"><span class="v-who-k">{t.csYou}</span><span class="v-who-v">{c.you}</span></div>
          <div class="v-who-col"><span class="v-who-k">{t.csIns}</span><span class="v-who-v">{c.ins}</span></div>
          <div class="v-who-col"><span class="v-who-k">{t.csRe}</span><span class="v-who-v">{c.re}</span></div>
        </div>
      </div>
      <div class="v-card" style={{ padding: '8px 18px' }}>
        {rows.map((r) => (
          <div key={r.title} class="v-cons-row"><span class="v-ico v-ico--brand"><Icon n={r.icon} /></span><div class="v-stack-s" style={{ gap: '2px' }}><h3 class="v-h3">{r.title}</h3><p class="v-small">{r.body}</p></div></div>
        ))}
      </div>
      <label class="v-check"><input type="checkbox" checked={tick} onChange={(e) => setTick(e.currentTarget.checked)} /><span>{c.agree}</span></label>
      {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
      <div class="v-stack-s">
        <button class="v-btn v-btn--primary" type="button" disabled={!tick || busy} onClick={() => void accept()}>{busy ? <Spinner /> : null}{t.csCta}</button>
        <button class="v-btn v-btn--quiet" type="button" onClick={skip}>{app.afterConsent.current ? t.cancel : c.skip}</button>
      </div>
      <div class="v-stack-s">
        <p class="v-xs">{t.csSeparate} {t.csLegal}</p>
        <a class="v-link" href="/api/consents/record" download style={{ alignSelf: 'flex-start' }}><Icon n="download" s={16} />{t.csDownload}</a>
      </div>
    </>
  )
}

// ───────────── sensor ─────────────

export function Connect() {
  const app = useApp()
  const { t, me } = app
  if (!me) return null
  const connected = me.dexcom.status === 'connected'
  const toDexcom = () => {
    if (consentOf(me, 'dexcom')?.status !== 'given') { app.unskip('consent:dexcom'); app.afterConsent.current = '/dexcom'; app.nav('/consent/dexcom'); return }
    app.nav('/dexcom')
  }
  const skip = () => { app.skip('connect'); void app.nextStep(me).then((p) => app.nav(p)) }
  return (
    <>
      <div class="v-stack-s"><h1 class="v-h1">{t.cnTitle}</h1><p class="v-lead">{t.cnBody}</p></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
        <div class="v-card v-card--raised v-stack" style={{ justifyContent: 'space-between' }}>
          <div class="v-stack-s">
            <div class="v-between"><span class="v-ico v-ico--brand"><Icon n="sensor" /></span>{connected && <span class="v-pill v-pill--ok"><span class="v-dot" />{t.cnDexOn}</span>}</div>
            <span class="v-h3" style={{ marginTop: '6px' }}>Dexcom</span>
            <span class="v-xs">{connected && me.dexcom.device ? me.dexcom.device : t.cnDexSub}</span>
          </div>
          {connected
            ? <button class="v-btn v-btn--secondary" type="button" onClick={skip} style={{ minHeight: '48px', fontSize: '15px', padding: '8px 12px' }}>{t.cont}</button>
            : <button class="v-btn v-btn--primary" type="button" onClick={toDexcom} style={{ minHeight: '48px', fontSize: '15px', padding: '8px 12px' }}>{t.cnDexBtn}</button>}
        </div>
        <div class="v-card v-stack" style={{ justifyContent: 'space-between', background: 'var(--surface-2)', boxShadow: 'none' }} aria-disabled="true">
          <div class="v-stack-s">
            <div class="v-between"><span class="v-ico"><Icon n="sensor" /></span><span class="v-pill v-pill--accent">{t.cnSoon}</span></div>
            <span class="v-h3" style={{ marginTop: '6px', color: 'var(--ink-2)' }}>FreeStyle Libre</span>
            <span class="v-xs">Abbott</span>
          </div>
          <button class="v-btn v-btn--primary" type="button" disabled style={{ minHeight: '48px', fontSize: '15px', padding: '8px 12px' }}>{t.cnLibreBtn}</button>
        </div>
      </div>
      <div class="v-msg"><Icon n="info" /><span>{t.cnNote}</span></div>
      {!connected && <button class="v-btn v-btn--quiet" type="button" onClick={skip}>{t.cnSkip}</button>}
    </>
  )
}

/** The one screen before leaving for Dexcom's own sign-in page. */
export function DexcomGo() {
  const app = useApp()
  const { t } = app
  const [going, setGoing] = useState(false)
  return (
    <>
      <span class="v-ico v-ico--brand v-ico--lg"><Icon n="sensor" /></span>
      <div class="v-stack-s"><h1 class="v-h1">{t.dxGoTitle}</h1><p class="v-lead">{t.dxGoBody}</p></div>
      <div class="v-card v-stack-s">
        <h2 class="v-h3">{t.dxReads}</h2>
        <ul class="v-stack-s">
          {[t.dxPermYes1, t.dxPermYes2].map((x) => <li key={x} class="v-small" style={{ display: 'flex', gap: '10px', color: 'var(--ink)' }}><Icon n="check" s={16} style={{ marginTop: '3px', color: 'var(--ok)' }} />{x}</li>)}
        </ul>
        <p class="v-small" style={{ marginTop: '6px' }}>{t.dxPermNo} {t.dxPermRevoke}</p>
      </div>
      <div class="v-msg v-msg--brand"><Icon n="lock" /><span>{t.dxNoPwd}</span></div>
      <div class="v-stack-s">
        <a class="v-btn v-btn--primary" href="/auth/dexcom/start" aria-disabled={going ? 'true' : 'false'} onClick={() => setGoing(true)}>{going ? <><Spinner />{t.dxGoing}</> : <>{t.dxGoBtn}<Icon n="ext" /></>}</a>
        <button class="v-btn v-btn--quiet" type="button" onClick={() => app.goBack('/connect')}>{t.cancel}</button>
      </div>
    </>
  )
}

/** Back from Dexcom: /connected?dexcom=ok|denied|error. */
export function Connected() {
  const app = useApp()
  const { t, me, lang } = app
  const [result] = useState(() => new URLSearchParams(location.search).get('dexcom'))
  const [next, setNext] = useState<string | null>(null)
  useEffect(() => {
    if (result !== 'ok') {
      app.toast(result === 'denied' ? t.dxDenied : t.dxErr)
      app.nav('/connect', { replace: true })
      return
    }
    history.replaceState(history.state, '', '/connected')
    let live = true
    void api<Me>('/api/me').then((fresh) => { if (!live) return; app.setMe(fresh); void app.nextStep(fresh).then((p) => live && setNext(p)) }, () => {})
    return () => { live = false }
  }, [])
  if (result !== 'ok' || !me) return <Skeleton />
  const dx = me.dexcom
  return (
    <>
      <div class="v-stack-l" style={{ alignItems: 'center', textAlign: 'center', paddingTop: '8px' }}>
        <div class="v-medal"><Icon n="check" style={{ width: '44px', height: '44px', color: 'var(--brand-text)', strokeWidth: 2.2 }} /></div>
        <div class="v-stack-s"><h1 class="v-h1">{t.dxOkTitle}</h1><p class="v-lead">{t.dxOkBody}</p></div>
      </div>
      <dl class="v-kv v-card">
        <dt>{t.dxDevice}</dt><dd>{dx.device ?? 'Dexcom'}</dd>
        <dt>{t.dxRange}</dt><dd>{dx.dataFrom && dx.dataTo ? fill(t.dxRangeV, { a: fmtDate(dx.dataFrom, lang), b: fmtDate(dx.dataTo, lang) }) : t.dxNoData}</dd>
        {dx.lastSyncAt && <><dt>{t.dxSync}</dt><dd>{fmtWhen(dx.lastSyncAt, lang)}</dd></>}
      </dl>
      <button class="v-btn v-btn--primary" type="button" disabled={!next} onClick={() => next && app.nav(next)}>{next === '/lab' ? t.dxNext : t.dxHome}<Icon n="arrow" /></button>
    </>
  )
}

// ───────────── lab result ─────────────

const MAX_BYTES = 10 * 1024 * 1024

export function LabUpload() {
  const app = useApp()
  const { t, me } = app
  const photo = useRef<HTMLInputElement>(null)
  const pdf = useRef<HTMLInputElement>(null)
  const [reading, setReading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (me && consentOf(me, 'lab')?.status !== 'given') { app.unskip('consent:lab'); app.afterConsent.current = '/lab'; app.nav('/consent/lab', { replace: true }) }
  }, [])
  const upload = async (file: File | undefined) => {
    if (!file) return
    setError('')
    if (file.size > MAX_BYTES) { setError(t.luTooBig); return }
    if (!/^image\//.test(file.type) && file.type !== 'application/pdf') { setError(t.luType); return }
    const form = new FormData()
    form.append('file', file)
    setReading(true)
    try {
      const draft = await api<LabDraft>('/api/labs', { method: 'POST', form })
      app.lab.current = { file, draft }
      app.nav('/lab/confirm')
    } catch (e) {
      setError(e instanceof ApiError && e.status === 413 ? t.luTooBig : e instanceof ApiError && e.status === 415 ? t.luType : t.luErr)
    } finally { setReading(false) }
  }
  const later = () => { app.skip('lab'); app.nav('/home') }
  return (
    <>
      <div class="v-stack-s"><h1 class="v-h1">{t.luTitle}</h1><p class="v-lead">{t.luBody}</p></div>
      <input ref={photo} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void upload(e.currentTarget.files?.[0]); e.currentTarget.value = '' }} />
      <input ref={pdf} type="file" accept="application/pdf,image/*" hidden onChange={(e) => { void upload(e.currentTarget.files?.[0]); e.currentTarget.value = '' }} />
      {reading ? (
        <div class="v-card v-card--raised v-stack" aria-busy="true" role="status">
          <div class="v-doc" style={{ height: '220px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div class="v-skel" style={{ height: '14px', width: '50%' }} /><div class="v-skel" style={{ height: '10px', width: '80%' }} /><div class="v-skel" style={{ height: '10px', width: '70%' }} /><div class="v-skel" style={{ height: '10px', width: '85%' }} /><div class="v-skel" style={{ height: '10px', width: '40%' }} /><div class="v-skel" style={{ height: '10px', width: '65%' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, height: '2px', background: 'var(--prog)', boxShadow: '0 0 16px var(--prog)', top: '40%', animation: 'v-scan 1.2s ease-in-out infinite alternate' }} />
          </div>
          <div><p class="v-h3">{t.luReading}</p><p class="v-xs">{t.luReadingSub}</p></div>
        </div>
      ) : (
        <div class="v-stack-l">
          <div class="v-grid2">
            <button class="v-card v-card--raised" type="button" onClick={() => photo.current?.click()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', textAlign: 'left', minHeight: '148px', cursor: 'pointer', color: 'var(--ink)' }}>
              <span class="v-ico v-ico--brand"><Icon n="camera" /></span>
              <span class="v-h3" style={{ marginTop: 'auto' }}>{t.luPhoto}</span><span class="v-xs">{t.luPhotoSub}</span>
            </button>
            <button class="v-card v-card--raised" type="button" onClick={() => pdf.current?.click()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', textAlign: 'left', minHeight: '148px', cursor: 'pointer', color: 'var(--ink)' }}>
              <span class="v-ico v-ico--brand"><Icon n="upload" /></span>
              <span class="v-h3" style={{ marginTop: 'auto' }}>{t.luPdf}</span><span class="v-xs">{t.luPdfSub}</span>
            </button>
          </div>
          {error && <div class="v-msg v-msg--accent" role="alert"><Icon n="info" /><span>{error}</span></div>}
          <div class="v-card v-card--flat">
            <h2 class="v-h3" style={{ marginBottom: '8px' }}>{t.luTips}</h2>
            <ul class="v-stack-s">{t.luTipList.map((tip) => <li key={tip} class="v-small" style={{ display: 'flex', gap: '10px' }}><Icon n="check" s={16} style={{ marginTop: '3px', color: 'var(--brand-text)' }} />{tip}</li>)}</ul>
          </div>
          <a class="v-link" href="/demo/analisis-hba1c-ejemplo.pdf" download style={{ alignSelf: 'flex-start' }}><Icon n="download" s={16} />{t.luSample}</a>
          <button class="v-btn v-btn--quiet" type="button" onClick={later}>{t.luLater}</button>
        </div>
      )}
    </>
  )
}

/** Renders one page of a PDF into an image URL, with pdf.js loaded only when a PDF needs it. */
async function pdfPage(file: File, page: number): Promise<string> {
  const pdfjs = await import('pdfjs-dist')
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js'
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) })
  const doc = await task.promise
  try {
    const p = await doc.getPage(Math.min(Math.max(1, page), doc.numPages))
    const viewport = p.getViewport({ scale: 2 })
    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    await p.render({ canvas, viewport }).promise
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('The PDF page could not be drawn')
    return URL.createObjectURL(blob)
  } finally { void task.destroy() }
}

/** Image URLs per page of the uploaded document: the photo itself, or each PDF page drawn once. */
function usePages(file: File | null, spots: readonly (Spot | null)[]): Record<number, string> {
  const [pages, setPages] = useState<Record<number, string>>({})
  useEffect(() => {
    if (!file) return
    const urls: string[] = []
    let live = true
    if (file.type !== 'application/pdf') {
      const url = URL.createObjectURL(file)
      urls.push(url)
      setPages({ 1: url })
    } else {
      const wanted = [...new Set(spots.filter((s) => s !== null).map((s) => s.page))]
      void Promise.all(wanted.map(async (n) => [n, await pdfPage(file, n)] as const)).then((done) => {
        for (const [, url] of done) urls.push(url)
        if (live) setPages(Object.fromEntries(done))
      }, () => {})
    }
    return () => { live = false; urls.forEach((u) => URL.revokeObjectURL(u)) }
  }, [file])
  return pages
}

export function LabConfirm() {
  const app = useApp()
  const { t, lang } = app
  const held = app.lab.current
  const draft = held?.draft ?? null
  const [edit, setEdit] = useState(draft ? draft.value === null || draft.labName === null || draft.testDate === null : false)
  const [value, setValue] = useState(draft?.value !== null && draft?.value !== undefined ? String(draft.value).replace('.', lang === 'es' ? ',' : '.') : '')
  const [labName, setLabName] = useState(draft?.labName ?? '')
  const [testDate, setTestDate] = useState(draft?.testDate ?? '')
  const [errors, setErrors] = useState<{ v?: string; l?: string; d?: string }>({})
  const [busy, setBusy] = useState(false)
  const pages = usePages(held?.file ?? null, draft ? [draft.spots.value, draft.spots.labName, draft.spots.testDate] : [])
  useEffect(() => { if (!draft) app.nav('/lab', { replace: true }) }, [])
  if (!draft) return null
  const done = (lab: LabView) => {
    app.lab.current = null
    if (lab.status === 'verified') { app.toast(t.lcDone); app.nav('/home') } else app.nav('/lab/review', { replace: true })
  }
  const confirm = async () => {
    setBusy(true)
    try { done(await api<LabView>(`/api/labs/${encodeURIComponent(draft.id)}/confirm`, { method: 'POST', json: {} })) } catch { app.toast(t.errGeneric) } finally { setBusy(false) }
  }
  const correct = async () => {
    const v = Number(value.replace(',', '.'))
    const today = new Date()
    const found = {
      ...(Number.isFinite(v) && v >= 3 && v <= 20 ? {} : { v: t.lcValueErr }),
      ...(labName.trim() ? {} : { l: t.lcLabErr }),
      ...(/^\d{4}-\d{2}-\d{2}$/.test(testDate) && toDate(testDate) <= today ? {} : { d: t.lcDateErr }),
    }
    setErrors(found)
    if (Object.keys(found).length) return
    setBusy(true)
    try { done(await api<LabView>(`/api/labs/${encodeURIComponent(draft.id)}/correct`, { method: 'POST', json: { value: v, labName: labName.trim(), testDate } })) } catch { app.toast(t.errGeneric) } finally { setBusy(false) }
  }
  const rows = [
    { label: t.lcValue, spot: draft.spots.value, shown: draft.value !== null ? fmtPct(draft.value, lang, 1) : t.lcNotFound,
      input: <input class="v-input v-num" inputMode="decimal" value={value} onInput={(e) => setValue(e.currentTarget.value)} aria-label={t.lcValue} aria-invalid={errors.v ? 'true' : 'false'} style={{ minHeight: '44px' }} />, err: errors.v },
    { label: t.lcLab, spot: draft.spots.labName, shown: draft.labName ?? t.lcNotFound,
      input: <input class="v-input" value={labName} onInput={(e) => setLabName(e.currentTarget.value)} aria-label={t.lcLab} aria-invalid={errors.l ? 'true' : 'false'} style={{ minHeight: '44px' }} />, err: errors.l },
    { label: t.lcDate, spot: draft.spots.testDate, shown: draft.testDate ? fmtDate(draft.testDate, lang) : t.lcNotFound,
      input: <input class="v-input v-num" type="date" value={testDate} max={new Date().toISOString().slice(0, 10)} onInput={(e) => setTestDate(e.currentTarget.value)} aria-label={t.lcDate} aria-invalid={errors.d ? 'true' : 'false'} style={{ minHeight: '44px' }} />, err: errors.d },
  ]
  return (
    <>
      <div class="v-msg v-msg--ok"><Icon n="check" /><span>{t.lcRead}</span></div>
      <div class="v-stack-s"><h1 class="v-h1">{t.lcTitle}</h1><p class="v-lead">{t.lcBody}</p></div>
      {draft.lowConfidence && !edit && <div class="v-msg v-msg--accent"><Icon n="info" /><span>{t.lcLow}</span></div>}
      <div class="v-card" style={{ padding: '8px 18px' }}>
        <div class="v-list">
          {rows.map((r) => (
            <div key={r.label} class="v-row" style={{ alignItems: 'center' }}>
              <Crop src={r.spot ? pages[r.spot.page] ?? null : null} spot={r.spot} label={r.label} noSpot={t.lcNoSpot} />
              <div class="v-row-main">
                <span class="v-row-s">{r.label}</span>
                {edit ? r.input : <span class="v-row-t" style={{ fontSize: '18px' }}>{r.shown}</span>}
                {edit && r.err && <span class="v-err">{r.err}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {edit ? (
        <div class="v-stack-s">
          <div class="v-msg"><Icon n="info" /><span>{t.lcFixNote}</span></div>
          <button class="v-btn v-btn--primary" type="button" disabled={busy} onClick={() => void correct()}>{busy ? <Spinner /> : null}{t.lcSend}</button>
          {draft.value !== null && draft.labName !== null && draft.testDate !== null && <button class="v-btn v-btn--quiet" type="button" onClick={() => setEdit(false)}>{t.cancel}</button>}
        </div>
      ) : (
        <div class="v-stack-s">
          <button class="v-btn v-btn--primary" type="button" disabled={busy} onClick={() => void confirm()}>{busy ? <Spinner /> : null}{t.lcYes}</button>
          <button class="v-btn v-btn--secondary" type="button" onClick={() => setEdit(true)}>{t.lcFix}</button>
        </div>
      )}
      <p class="v-xs">{t.lcKeep}</p>
    </>
  )
}

export function LabReview() {
  const app = useApp()
  const { t, lang } = app
  const labs = useLoad(() => api<readonly LabView[]>('/api/labs'))
  if (labs.loading && !labs.data) return <Skeleton />
  if (labs.error && !labs.data) return <ErrorState error={labs.error} onRetry={labs.reload} />
  const lab = [...(labs.data ?? [])].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))[0] ?? null
  const verified = lab?.status === 'verified'
  return (
    <div class="v-stack-l" style={{ paddingTop: '8px' }}>
      <span class={`v-ico v-ico--lg ${verified ? 'v-ico--ok' : 'v-ico--accent'}`}><Icon n={verified ? 'check' : 'clock'} /></span>
      <div class="v-stack-s"><h1 class="v-h1">{verified ? t.lcDone : t.lrTitle}</h1>{!verified && <p class="v-lead">{t.lrBody}</p>}</div>
      <div class="v-card">
        <ol class="v-tl">
          <li class="v-tl-i is-done"><span class="v-tl-dot"><Icon n="check" s={16} /></span><span class="v-stack-s" style={{ gap: 0 }}><span class="v-h3">{t.lrS1}</span>{lab && <span class="v-xs">{fmtWhen(lab.uploadedAt, lang).replace(/^./, (c) => c.toUpperCase())}</span>}</span></li>
          <li class={`v-tl-i ${verified ? 'is-done' : 'is-now'}`}><span class="v-tl-dot">{verified && <Icon n="check" s={16} />}</span><span class="v-stack-s" style={{ gap: 0 }}><span class="v-h3">{t.lrS2}</span><span class="v-xs">{t.lrS2d}</span></span></li>
          <li class={`v-tl-i ${verified ? 'is-done' : ''}`}><span class="v-tl-dot">{verified && <Icon n="check" s={16} />}</span><span class="v-stack-s" style={{ gap: 0 }}><span class="v-h3" style={verified ? undefined : { color: 'var(--ink-3)' }}>{t.lrS3}</span><span class="v-xs">{t.lrS3d}</span></span></li>
        </ol>
      </div>
      <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/home')}>{t.lrHome}</button>
    </div>
  )
}
